import { Injectable } from '@angular/core';

export interface RetryConfig {
  maxAttempts: number;
  delayMs: number;
  backoffMultiplier: number;
  onRetry?: (attempt: number, maxAttempts: number) => void;
}

@Injectable({
  providedIn: 'root'
})
export class RetryService {
  private readonly defaultConfig: RetryConfig = {
    maxAttempts: 3,
    delayMs: 1000,
    backoffMultiplier: 2
  };

  /**
   * Retry a condition check with exponential backoff
   * @param conditionFn Function that returns true when condition is met
   * @param config Retry configuration
   * @param errorMessage Error message to throw if max attempts reached
   */
  async retryUntilCondition(
    conditionFn: () => boolean,
    config?: Partial<RetryConfig>,
    errorMessage: string = 'Condition not met after maximum retry attempts'
  ): Promise<void> {
    const finalConfig = { ...this.defaultConfig, ...config };
    let attempt = 0;
    let delay = finalConfig.delayMs;

    while (attempt < finalConfig.maxAttempts) {
      if (conditionFn()) {
        return;
      }

      attempt++;
      
      if (attempt >= finalConfig.maxAttempts) {
        throw new Error(errorMessage);
      }

      // Call onRetry callback if provided
      if (finalConfig.onRetry) {
        finalConfig.onRetry(attempt, finalConfig.maxAttempts);
      }

      console.log(`Retry attempt ${attempt}/${finalConfig.maxAttempts}...`);
      
      // Wait before next attempt
      await this.delay(delay);
      
      // Exponential backoff
      delay *= finalConfig.backoffMultiplier;
    }
  }

  /**
   * Retry an async operation with exponential backoff
   * @param operationFn Async function to retry
   * @param config Retry configuration
   */
  async retryOperation<T>(
    operationFn: () => Promise<T>,
    config?: Partial<RetryConfig>
  ): Promise<T> {
    const finalConfig = { ...this.defaultConfig, ...config };
    let attempt = 0;
    let delay = finalConfig.delayMs;
    let lastError: Error | null = null;

    while (attempt < finalConfig.maxAttempts) {
      try {
        return await operationFn();
      } catch (error) {
        lastError = error as Error;
        attempt++;
        
        if (attempt >= finalConfig.maxAttempts) {
          throw new Error(
            `Operation failed after ${finalConfig.maxAttempts} attempts. Last error: ${lastError.message}`
          );
        }

        // Call onRetry callback if provided
        if (finalConfig.onRetry) {
          finalConfig.onRetry(attempt, finalConfig.maxAttempts);
        }

        console.log(
          `Operation failed, retrying... (attempt ${attempt}/${finalConfig.maxAttempts})`,
          error
        );
        
        // Wait before next attempt
        await this.delay(delay);
        
        // Exponential backoff
        delay *= finalConfig.backoffMultiplier;
      }
    }

    throw lastError || new Error('Operation failed');
  }

  /**
   * Retry with custom validation
   * @param operationFn Async function to retry
   * @param validationFn Function to validate the result
   * @param config Retry configuration
   */
  async retryWithValidation<T>(
    operationFn: () => Promise<T>,
    validationFn: (result: T) => boolean,
    config?: Partial<RetryConfig>,
    errorMessage: string = 'Operation result validation failed'
  ): Promise<T> {
    const finalConfig = { ...this.defaultConfig, ...config };
    let attempt = 0;
    let delay = finalConfig.delayMs;

    while (attempt < finalConfig.maxAttempts) {
      try {
        const result = await operationFn();
        
        if (validationFn(result)) {
          return result;
        }

        attempt++;
        
        if (attempt >= finalConfig.maxAttempts) {
          throw new Error(errorMessage);
        }

        // Call onRetry callback if provided
        if (finalConfig.onRetry) {
          finalConfig.onRetry(attempt, finalConfig.maxAttempts);
        }

        console.log(
          `Validation failed, retrying... (attempt ${attempt}/${finalConfig.maxAttempts})`
        );
        
        await this.delay(delay);
        delay *= finalConfig.backoffMultiplier;
      } catch (error) {
        attempt++;
        
        if (attempt >= finalConfig.maxAttempts) {
          throw error;
        }

        if (finalConfig.onRetry) {
          finalConfig.onRetry(attempt, finalConfig.maxAttempts);
        }

        console.log(
          `Operation error, retrying... (attempt ${attempt}/${finalConfig.maxAttempts})`,
          error
        );
        
        await this.delay(delay);
        delay *= finalConfig.backoffMultiplier;
      }
    }

    throw new Error(errorMessage);
  }

  /**
   * Create delay promise
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}